import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import ContextualTour from '@/components/feature/ContextualTour';
import { supabase } from '@/lib/supabase';
import CampoEndereco from '@/components/base/CampoEndereco';
import { ENDERECO_VAZIO, type Endereco } from '@/lib/endereco';
import {
  carregarUsoDoPlano,
  descreverUso,
  interpretarErroDeLimite,
  invalidarUsoDoPlano,
  mensagemDeUpgrade,
  type UsoDoPlano,
} from '@/lib/planoLimites';
import { useAuth } from '@/hooks/useAuth';
import { useEGestor } from '@/lib/permissoes';
import { LIMITE_TEXTO } from '@/lib/mascaras';

interface Condominio {
  id: string;
  nome: string;
  localizacao: string;
  ativo: boolean;
  /** Torres ativas do condomínio, contadas no banco. Não é campo de cadastro. */
  blocos: number;
  /** Imóveis ativos do condomínio, contados no banco. Não é campo de cadastro. */
  unidades: number;
}

// Blocos e unidades são contados, não lidos das colunas homônimas de
// `condominios` — que existem, mas guardam o que alguém digitou no cadastro e
// nunca mais revisou (P-29). O agregado do PostgREST resolve na mesma consulta.
const SELECT_CONDOMINIO = 'id, nome, localizacao, ativo, torres(count), imoveis(count)';

interface LinhaDeCondominio {
  id: string;
  nome: string;
  localizacao: string;
  ativo: boolean;
  torres?: Array<{ count: number }> | { count: number } | null;
  imoveis?: Array<{ count: number }> | { count: number } | null;
}

/** O agregado vem como array de uma linha ou como objeto, conforme a versão. */
function contar(relacao: LinhaDeCondominio['torres']): number {
  if (!relacao) return 0;
  if (Array.isArray(relacao)) return relacao[0]?.count ?? 0;
  return relacao.count ?? 0;
}

function comContagens(linha: LinhaDeCondominio): Condominio {
  return {
    id: linha.id,
    nome: linha.nome,
    localizacao: linha.localizacao,
    ativo: linha.ativo,
    blocos: contar(linha.torres),
    unidades: contar(linha.imoveis),
  };
}

const condoTourSteps = [
  {
    title: 'Bem-vindo ao cadastro de condomínios',
    description:
      'Aqui você gerencia todos os condomínios da sua carteira. Cada condomínio concentra torres, imóveis, funcionários, infraestrutura e documentos.',
    icon: 'ri-building-line',
  },
  {
    title: 'Criar novo condomínio',
    description:
      'Clique no botão "Novo Condomínio" no topo para cadastrar um condomínio do zero. Você vai preencher dados como razão social, endereço, síndico e administradora.',
    icon: 'ri-add-circle-line',
    highlightSelector: 'button',
  },
  {
    title: 'Buscar e filtrar',
    description:
      'Use o campo de busca para localizar rapidamente um condomínio pelo nome ou cidade. A lista é filtrada em tempo real conforme você digita.',
    icon: 'ri-search-line',
  },
  {
    title: 'Ativar e Inativar Condomínios',
    description:
      'Ao tentar excluir um condomínio, você pode optar por apenas desativá-lo. Condomínios inativos deixam de contar nos acompanhamentos, mas todos os dados (torres, imóveis, funcionários, documentos) são preservados. Você pode reativá-los a qualquer momento.',
    icon: 'ri-eye-off-line',
  },
  {
    title: 'Acessar os detalhes',
    description:
      'Clique no nome de qualquer condomínio para abrir a ficha completa. Lá dentro você encontra as abas: Resumo, Torres, Imóveis, Funcionários e muito mais.',
    icon: 'ri-folder-open-line',
  },
];

type ToastType = 'success' | 'info' | 'error';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

export default function CondominiosPage() {
  const { user, effectiveAccountId } = useAuth();
  const [busca, setBusca] = useState('');
  const [condominios, setCondominios] = useState<Condominio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usoDoPlano, setUsoDoPlano] = useState<UsoDoPlano | null>(null);
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [condoSelecionado, setCondoSelecionado] = useState<Condominio | null>(null);
  // Confirmação por digitação: o nome tem de ser reescrito à mão. Um "tem
  // certeza?" é clicado no automático; reescrever o nome obriga a olhar para
  // qual condomínio está prestes a sumir, com tudo que pende dele.
  const [confirmacaoNome, setConfirmacaoNome] = useState('');
  const [excluindo, setExcluindo] = useState(false);

  // Excluir condomínio é do Gestor da conta. A policy no banco recusa qualquer
  // outro papel; aqui a ação some da tela para não oferecer o que será negado.
  const { eGestor } = useEGestor(effectiveAccountId);

  // Comparação tolerante a espaço e caixa: a exigência é reconhecer o nome, não
  // acertar a digitação.
  const nomeConfere =
    !!condoSelecionado &&
    confirmacaoNome.trim().toLocaleLowerCase('pt-BR') ===
      condoSelecionado.nome.trim().toLocaleLowerCase('pt-BR');
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);

  // Create form state
  // Blocos e unidades saíram do cadastro (P-29): quem os produz é a torre e o
  // imóvel, e o número digitado aqui nunca mais era atualizado.
  const [createForm, setCreateForm] = useState({ nome: '' });
  // O endereco estruturado substitui o antigo campo livre "Localizacao", que
  // agora e derivado de cidade/estado por gatilho no banco (migration 0010).
  const [endereco, setEndereco] = useState<Endereco>(ENDERECO_VAZIO);

  const adicionarToast = (message: string, type: ToastType = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  };

  useEffect(() => {
    if (toasts.length > 0) {
      const timer = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toasts[0].id));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [toasts]);

  // Fetch condominios from Supabase
  const fetchCondominios = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('condominios')
        .select(SELECT_CONDOMINIO)
        .order('nome', { ascending: true });

      if (fetchError) {
        setError(fetchError.message);
      } else {
        setCondominios((data || []).map(comContagens));
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar condomínios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarUsoDoPlano().then(setUsoDoPlano);
  }, []);

  useEffect(() => {
    fetchCondominios();
  }, []);

  const abrirModalExclusao = (condo: Condominio) => {
    setCondoSelecionado(condo);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setCondoSelecionado(null);
    setConfirmacaoNome('');
  };

  const desativarCondominio = async () => {
    if (!condoSelecionado) return;
    const { error: updateError } = await supabase
      .from('condominios')
      .update({ ativo: false })
      .eq('id', condoSelecionado.id);

    if (updateError) {
      adicionarToast(`Erro ao desativar: ${updateError.message}`, 'error');
      fecharModal();
      return;
    }

    setCondominios((prev) =>
      prev.map((c) => (c.id === condoSelecionado.id ? { ...c, ativo: false } : c)),
    );
    adicionarToast(
      `"${condoSelecionado.nome}" foi desativado e não conta mais nos acompanhamentos.`,
      'info',
    );
    fecharModal();
  };

  const excluirCondominio = async () => {
    if (!condoSelecionado || !nomeConfere) return;

    setExcluindo(true);
    const { error: deleteError } = await supabase
      .from('condominios')
      .delete()
      .eq('id', condoSelecionado.id);
    setExcluindo(false);

    if (deleteError) {
      adicionarToast(`Erro ao excluir: ${deleteError.message}`, 'error');
      fecharModal();
      return;
    }

    setCondominios((prev) => prev.filter((c) => c.id !== condoSelecionado.id));
    adicionarToast(
      `"${condoSelecionado.nome}" foi excluído, junto com torres, imóveis, funcionários e documentos.`,
      'success',
    );
    fecharModal();
  };

  const reativarCondominio = async (condo: Condominio) => {
    const { error: updateError } = await supabase
      .from('condominios')
      .update({ ativo: true })
      .eq('id', condo.id);

    if (updateError) {
      adicionarToast(`Erro ao reativar: ${updateError.message}`, 'error');
      return;
    }

    setCondominios((prev) => prev.map((c) => (c.id === condo.id ? { ...c, ativo: true } : c)));
    adicionarToast(`"${condo.nome}" foi reativado.`, 'success');
  };

  const criarCondominio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.nome.trim()) return;

    setCreating(true);
    const accountId = effectiveAccountId || null;

    const { comentario, ...enderecoEstruturado } = endereco;

    const novoCondominio = {
      nome: createForm.nome.trim(),
      // `localizacao` nao vai daqui: o banco deriva de cidade/estado.
      endereco: enderecoEstruturado,
      comentarios: comentario?.trim() || null,
      ativo: true,
      account_id: accountId,
    };

    const { data, error: insertError } = await supabase
      .from('condominios')
      .insert([novoCondominio])
      .select(SELECT_CONDOMINIO)
      .single();

    if (insertError) {
      // O gatilho de limite de plano devolve um erro identificavel: vale uma
      // mensagem que diga o que fazer, nao a mensagem crua do banco.
      const limite = interpretarErroDeLimite(insertError);
      if (limite) {
        adicionarToast(mensagemDeUpgrade(limite), 'error');
      } else {
        adicionarToast(`Erro ao criar: ${insertError.message}`, 'error');
      }
      setCreating(false);
      return;
    }

    invalidarUsoDoPlano();
    void carregarUsoDoPlano(true).then(setUsoDoPlano);

    if (data) {
      setCondominios((prev) => [...prev, comContagens(data)]);
      adicionarToast(`"${data.nome}" criado com sucesso!`, 'success');
    }

    setCreateForm({ nome: '' });
    setEndereco(ENDERECO_VAZIO);
    setShowCreateModal(false);
    setCreating(false);
  };

  const ativos = condominios.filter((c) => c.ativo);
  const inativos = condominios.filter((c) => !c.ativo);
  const listaBase = mostrarInativos ? condominios : ativos;

  const filtrados = listaBase.filter(
    (c) =>
      c.nome.toLowerCase().includes(busca.toLowerCase()) ||
      c.localizacao.toLowerCase().includes(busca.toLowerCase()),
  );

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-foreground-500">Carregando condomínios...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4 text-center max-w-md">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <i className="ri-error-warning-line text-red-500 text-2xl"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-900">Erro ao carregar</h2>
            <p className="text-sm text-foreground-600">{error}</p>
            <button
              onClick={fetchCondominios}
              className="px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">Condomínios</h1>
            <p className="text-sm text-foreground-600 mt-1">
              {condominios.length} condomínio(s) cadastrado(s) — {ativos.length} ativo(s)
              {usoDoPlano ? (
                <>
                  {' '}
                  ·{' '}
                  <span className="font-medium">
                    {descreverUso(usoDoPlano.condominios, 'condominios')}
                  </span>{' '}
                  no plano {usoDoPlano.plano?.nome ?? 'atual'}
                </>
              ) : null}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap shrink-0"
          >
            <i className="ri-add-line"></i>
            Novo Condomínio
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line"></i>
            </div>
            <input
              type="text"
              placeholder="Buscar por nome ou cidade..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
            />
          </div>
        </div>

        {/* Filter: show/hide inactive */}
        {inativos.length > 0 && (
          <div className="mb-4">
            <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={mostrarInativos}
                onChange={(e) => setMostrarInativos(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-5 h-5 rounded-md border-2 border-background-300 flex items-center justify-center shrink-0 transition-all duration-200 peer-checked:bg-primary-500 peer-checked:border-primary-500 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-300/40 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-background-50">
                <i className="ri-check-line text-background-50 text-[10px] leading-none scale-0 peer-checked:scale-100 transition-transform duration-200"></i>
              </div>
              <span className="text-sm text-foreground-600">
                Mostrar condomínios inativos ({inativos.length})
              </span>
            </label>
          </div>
        )}

        {/* Inactive condos section */}
        {mostrarInativos && inativos.length > 0 && (
          <div className="mb-6 p-4 bg-amber-50/60 border border-amber-200/60 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 flex items-center justify-center text-amber-600">
                <i className="ri-error-warning-line"></i>
              </div>
              <p className="text-sm font-medium text-amber-800">
                Condomínios desativados ({inativos.length})
              </p>
            </div>
            <p className="text-xs text-amber-700 ml-7">
              Estes condomínios não aparecem nos controles e deixam de contar nos acompanhamentos.
              Seus dados (torres, imóveis, funcionários, documentos) continuam preservados.
            </p>
          </div>
        )}

        {/* Table */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                    Nome
                  </th>
                  <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                    Localização
                  </th>
                  <th className="text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                    Blocos/Torres
                  </th>
                  <th className="text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                    Unidades
                  </th>
                  <th className="text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                    Status
                  </th>
                  <th className="text-right text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/50">
                {filtrados.map((condo) => (
                  <tr
                    key={condo.id}
                    className={`hover:bg-background-100/50 transition-colors ${!condo.ativo ? 'opacity-60 bg-amber-50/30' : ''}`}
                  >
                    <td className="px-5 py-4">
                      {condo.ativo ? (
                        <Link
                          to={`/condominios/${condo.id}/resumo`}
                          className="flex items-center gap-2.5 group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                            <i className="ri-building-line text-primary-600 text-sm"></i>
                          </div>
                          <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors">
                            {condo.nome}
                          </span>
                        </Link>
                      ) : (
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                            <i className="ri-building-line text-amber-500 text-sm"></i>
                          </div>
                          <span className="text-sm font-medium text-foreground-600">
                            {condo.nome}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-sm text-foreground-600">
                        <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                          <i className="ri-map-pin-line text-xs"></i>
                        </div>
                        {condo.localizacao}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-2 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                        {condo.blocos}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-2 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                        {condo.unidades}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      {condo.ativo ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                          Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {condo.ativo ? (
                          <>
                            <Link
                              to={`/condominios/${condo.id}/resumo`}
                              className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors"
                              title="Editar"
                            >
                              <i className="ri-pencil-line"></i>
                            </Link>
                            {eGestor && (
                              <button
                                onClick={() => abrirModalExclusao(condo)}
                                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                                title="Excluir"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            )}
                          </>
                        ) : (
                          <button
                            onClick={() => reativarCondominio(condo)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors whitespace-nowrap"
                            title="Reativar"
                          >
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-refresh-line"></i>
                            </div>
                            Reativar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtrados.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-building-line text-foreground-400 text-xl"></i>
                      </div>
                      <p className="text-sm text-foreground-500">Nenhum condomínio encontrado.</p>
                      {condominios.length === 0 && (
                        <button
                          onClick={() => setShowCreateModal(true)}
                          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-add-line"></i>
                          Criar primeiro condomínio
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Condominio Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowCreateModal(false)}
          ></div>
          <div className="relative bg-background-50 rounded-xl border border-background-200/70 shadow-lg w-full max-w-md overflow-hidden">
            <div className="p-6 pb-0">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
                  <i className="ri-building-line text-primary-600 text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-foreground-900">Novo Condomínio</h3>
                  <p className="text-sm text-foreground-600 mt-0.5">
                    Preencha os dados básicos. Torres e unidades vêm depois, na ficha do condomínio.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={criarCondominio} className="p-6 pt-4 flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Nome do Condomínio *
                </label>
                <input
                  type="text"
                  value={createForm.nome}
                  onChange={(e) => setCreateForm({ ...createForm, nome: e.target.value })}
                  required
                  placeholder="Ex: Residencial das Flores"
                  className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                />
              </div>
              <CampoEndereco
                valor={endereco}
                onChange={setEndereco}
                comComentario
                rotuloComentario="Comentário"
                idPrefixo="novo_condominio"
              />
              <p className="flex items-start gap-2 text-xs text-foreground-500 bg-background-100/60 border border-background-200/70 rounded-md px-3 py-2.5">
                <i className="ri-information-line text-foreground-400 mt-px"></i>
                <span>
                  Torres e unidades são cadastradas dentro do condomínio, na aba Torres. A lista
                  mostra as contagens reais.
                </span>
              </p>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating || !createForm.nome.trim()}
                  className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {creating ? 'Criando...' : 'Criar Condomínio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete/Deactivate Confirmation Modal */}
      {modalAberto && condoSelecionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={fecharModal}
          ></div>
          <div className="relative bg-background-50 rounded-xl border border-background-200/70 shadow-lg w-full max-w-md overflow-hidden">
            <div className="p-6 pb-0">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <i className="ri-error-warning-line text-red-600 text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-foreground-900">
                    Tem certeza que deseja excluir?
                  </h3>
                  <p className="text-sm text-foreground-600 mt-1">
                    A exclusão de{' '}
                    <strong className="text-foreground-800">{condoSelecionado.nome}</strong> irá
                    apagar <strong className="text-red-600">permanentemente</strong> todos os dados
                    relacionados:
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 mt-4">
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { icon: 'ri-building-2-line', label: 'Torres e andares' },
                  { icon: 'ri-home-4-line', label: 'Todos os imóveis' },
                  { icon: 'ri-user-settings-line', label: 'Funcionários' },
                  { icon: 'ri-file-list-3-line', label: 'Treinamentos' },
                  { icon: 'ri-tools-line', label: 'Infraestrutura' },
                  { icon: 'ri-attachment-2', label: 'Documentos e anexos' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-red-50/60 text-xs text-red-700"
                  >
                    <div className="w-4 h-4 flex items-center justify-center text-red-500">
                      <i className={`${item.icon} text-[11px]`}></i>
                    </div>
                    {item.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="mx-6 mt-4 p-3.5 rounded-lg bg-amber-50 border border-amber-200/70">
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center text-amber-500 shrink-0 mt-0.5">
                  <i className="ri-lightbulb-flash-line"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Que tal apenas desativar o condomínio?
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Ao desativar, ele deixa de contar nos acompanhamentos, mas{' '}
                    <strong className="text-amber-800">todos os dados são preservados</strong>. Você
                    pode reativá-lo quando quiser.
                  </p>
                </div>
              </div>
            </div>

            <div className="mx-6 mt-4">
              <label
                htmlFor="confirmacao_exclusao"
                className="block text-sm text-foreground-700 leading-relaxed"
              >
                Para excluir o condomínio, digite{' '}
                <strong className="text-foreground-900">{condoSelecionado.nome}</strong> abaixo e
                clique em confirmar.
              </label>
              <input
                id="confirmacao_exclusao"
                type="text"
                autoComplete="off"
                value={confirmacaoNome}
                onChange={(e) => setConfirmacaoNome(e.target.value)}
                placeholder={condoSelecionado.nome}
                maxLength={LIMITE_TEXTO}
                className={`w-full mt-2 px-3 py-2.5 rounded-md border text-sm ${
                  confirmacaoNome && !nomeConfere
                    ? 'border-red-300 bg-red-50'
                    : 'border-background-300/60 bg-background-50'
                }`}
              />
              {confirmacaoNome && !nomeConfere && (
                <p className="text-xs text-red-600 mt-1">
                  O nome digitado não confere com o do condomínio.
                </p>
              )}
            </div>

            <div className="p-6 pt-4 flex flex-col gap-2">
              <button
                onClick={desativarCondominio}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-off-line"></i>
                </div>
                Desativar Condomínio
              </button>
              <button
                onClick={excluirCondominio}
                disabled={!nomeConfere || excluindo}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-500"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-delete-bin-line"></i>
                </div>
                {excluindo ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
              <button
                onClick={fecharModal}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-foreground-700 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap mt-1"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      {toasts.length > 0 && (
        <div className="fixed bottom-6 right-6 z-[60] flex flex-col gap-2">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-md border text-sm max-w-sm animate-[slideInRight_0.3s_ease-out] ${
                toast.type === 'success'
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : toast.type === 'error'
                    ? 'bg-red-50 border-red-200 text-red-800'
                    : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}
            >
              <div
                className={`w-5 h-5 flex items-center justify-center shrink-0 ${
                  toast.type === 'success'
                    ? 'text-green-500'
                    : toast.type === 'error'
                      ? 'text-red-500'
                      : 'text-blue-500'
                }`}
              >
                <i
                  className={
                    toast.type === 'success'
                      ? 'ri-checkbox-circle-line'
                      : toast.type === 'error'
                        ? 'ri-close-circle-line'
                        : 'ri-information-line'
                  }
                ></i>
              </div>
              <span className="flex-1">{toast.message}</span>
              <button
                onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
                className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-600 shrink-0"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      <ContextualTour
        storageKey="tour_condominio_cadastro"
        steps={condoTourSteps}
        title="Cadastro de Condomínio"
      />
    </DashboardLayout>
  );
}
